import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createGalleryItems,
  deleteGalleryItem,
  deleteGalleryItems,
  fetchGallery,
  uploadAdminMedia
} from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminGalleryItem } from "@/features/admin/types";

import PaginationControls from "./PaginationControls";

const DEFAULT_ALBUMS = [
  "Tournoi inter-entreprises",
  "Assemblée générale",
  "Football",
  "Basketball",
  "Volleyball",
  "Tennis de table",
  "Culture",
  "Événements"
];
const MAX_GALLERY_MEDIA_FILES = 100;

type SelectedPreview = {
  id: string;
  url: string;
  name: string;
  mediaType: "image" | "video";
};

type AlbumGroup = {
  name: string;
  items: AdminGalleryItem[];
  cover: AdminGalleryItem;
  photosCount: number;
  videosCount: number;
};

function getFileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function toTitleCase(value: string) {
  return value
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function buildMediaTitle(album: string, filename: string, index: number, total: number) {
  const trimmedAlbum = album.trim();

  if (!trimmedAlbum) {
    return toTitleCase(filename);
  }

  if (total === 1) {
    return trimmedAlbum;
  }

  return `${trimmedAlbum} ${index + 1}`;
}

function formatAlbumLabel(value: string) {
  const matchingDefault = DEFAULT_ALBUMS.find((album) => album.toLowerCase() === value.toLowerCase());

  if (matchingDefault) {
    return matchingDefault;
  }

  return toTitleCase(value);
}

function normalizeAlbumValue(value: string) {
  return value.trim().replace(/\s+/g, " ");
}

function getAlbumFolder(value: string) {
  return normalizeAlbumValue(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function groupByAlbum(items: AdminGalleryItem[]) {
  return items.reduce<Record<string, AdminGalleryItem[]>>((groups, item) => {
    const album = item.category || "Sans album";
    groups[album] = groups[album] ? [...groups[album], item] : [item];
    return groups;
  }, {});
}

function buildAlbumGroups(items: AdminGalleryItem[]): AlbumGroup[] {
  return Object.entries(groupByAlbum(items))
    .map(([name, albumItems]) => ({
      name,
      items: albumItems,
      cover: albumItems.find((item) => item.mediaType === "image") ?? albumItems[0],
      photosCount: albumItems.filter((item) => item.mediaType === "image").length,
      videosCount: albumItems.filter((item) => item.mediaType === "video").length
    }))
    .sort((firstAlbum, secondAlbum) => formatAlbumLabel(firstAlbum.name).localeCompare(formatAlbumLabel(secondAlbum.name), "fr"));
}

function AlbumPreviewStrip({ items }: { items: AdminGalleryItem[] }) {
  const previewItems = items.slice(0, 6);
  const animatedItems = previewItems.length > 1 ? [...previewItems, ...previewItems] : previewItems;

  return (
    <div className="absolute inset-x-0 bottom-0 h-20 overflow-hidden bg-black/35 backdrop-blur-[1px]">
      <div className={`flex h-full gap-2 p-2 ${previewItems.length > 1 ? "animate-[album-marquee_16s_linear_infinite]" : ""}`}>
        {animatedItems.map((item, index) => (
          <div key={`${item.id}-${index}`} className="relative h-full w-28 flex-shrink-0 overflow-hidden rounded-xl border border-white/20 bg-black/40">
            {item.mediaType === "video" ? (
              <>
                <video src={item.src} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <i className="ri-play-fill text-lg text-white"></i>
                </div>
              </>
            ) : (
              <img src={item.src} alt={item.title} className="h-full w-full object-cover" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GalerieTab() {
  const ITEMS_PER_PAGE = 6;
  const ALBUMS_PER_PAGE = 3;
  const { request } = useAdminSession();
  const [items, setItems] = useState<AdminGalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingSelection, setDeletingSelection] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [form, setForm] = useState<{
    album: string;
    files: File[];
  }>({
    album: "Tournoi inter-entreprises",
    files: []
  });

  const [previews, setPreviews] = useState<SelectedPreview[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [albumPage, setAlbumPage] = useState(1);
  const [selectedAlbum, setSelectedAlbum] = useState("tous");
  const [selectedItem, setSelectedItem] = useState<AdminGalleryItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const loadGallery = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextItems = await fetchGallery(request);
      setItems(nextItems);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger la galerie";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    if (form.files.length === 0) {
      setPreviews([]);
      return;
    }

    const nextPreviews = form.files.map((file, index) => ({
      id: `${file.name}-${index}-${file.lastModified}`,
      url: URL.createObjectURL(file),
      name: file.name,
      mediaType: (file.type.startsWith("video/") ? "video" : "image") as SelectedPreview["mediaType"]
    }));

    setPreviews(nextPreviews);

    return () => {
      nextPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [form.files]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setForm({
      album: selectedAlbum === "tous" ? "Tournoi inter-entreprises" : selectedAlbum,
      files: []
    });
    setFileInputKey((current) => current + 1);
  };

  const appendFiles = (incomingFiles: File[]) => {
    const existingKeys = new Set(form.files.map((file) => getFileKey(file)));
    const mergedFiles = [...form.files];
    let ignoredCount = 0;

    incomingFiles.forEach((file) => {
      const key = getFileKey(file);

      if (existingKeys.has(key)) {
        ignoredCount += 1;
        return;
      }

      if (mergedFiles.length >= MAX_GALLERY_MEDIA_FILES) {
        ignoredCount += 1;
        return;
      }

      mergedFiles.push(file);
      existingKeys.add(key);
    });

    setForm({
      ...form,
      files: mergedFiles
    });

    if (ignoredCount > 0) {
      setError(`Tu peux sélectionner au maximum ${MAX_GALLERY_MEDIA_FILES} photos ou vidéos à la fois.`);
    } else {
      setError(null);
    }

    setFileInputKey((current) => current + 1);
  };

  const openFilePicker = () => {
    const input = document.getElementById("gallery-media-picker");
    if (input instanceof HTMLInputElement) {
      input.click();
    }
  };

  const removeSelectedFile = (previewId: string) => {
    setForm((current) => ({
      ...current,
      files: current.files.filter((file, index) => `${file.name}-${index}-${file.lastModified}` !== previewId)
    }));
  };

  const handleSave = async () => {
    const album = normalizeAlbumValue(form.album);

    if (!album) {
      setError("Indique l'album ou l'événement concerné avant l'envoi.");
      return;
    }

    if (form.files.length === 0) {
      setError("Sélectionne au moins une photo ou une vidéo avant l'envoi.");
      return;
    }

    if (form.files.length > MAX_GALLERY_MEDIA_FILES) {
      setError(`Tu peux publier au maximum ${MAX_GALLERY_MEDIA_FILES} photos ou vidéos à la fois.`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const albumFolder = getAlbumFolder(album) || "sans-album";
      const assets = await uploadAdminMedia(request, form.files, `asc-sonatel/gallery/${albumFolder}`);

      await createGalleryItems(
        request,
        assets.map((asset, index) => ({
          title: buildMediaTitle(album, form.files[index]?.name ?? asset.originalFilename, index, assets.length),
          category: album,
          image: asset.url,
          publicId: asset.publicId,
          mediaType: asset.resourceType === "video" ? "video" : "image"
        }))
      );

      const mediaLabel = assets.length > 1 ? "médias" : "média";
      setSelectedAlbum(album);
      setCurrentPage(1);
      setForm({
        album,
        files: []
      });
      setFileInputKey((current) => current + 1);
      showToast(`${assets.length} ${mediaLabel} publié(s) dans l'album ${album}`);
      await loadGallery();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Envoi impossible";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await deleteGalleryItem(request, itemId);
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(itemId);
        return next;
      });
      showToast("Média supprimé de la galerie et de Cloudinary");
      await loadGallery();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    }
  };

  const handleDeleteSelection = async () => {
    const ids = Array.from(selectedIds);

    if (ids.length === 0) {
      return;
    }

    setDeletingSelection(true);
    setError(null);

    try {
      await deleteGalleryItems(request, ids);
      setSelectedIds(new Set());
      showToast(`${ids.length} média(s) supprimé(s) de la galerie et de Cloudinary`);
      await loadGallery();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression multiple impossible";
      setError(message);
    } finally {
      setDeletingSelection(false);
    }
  };

  const toggleSelectedItem = (itemId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }

      return next;
    });
  };

  const albums = useMemo(() => {
    const fromItems = Array.from(new Set(items.map((item) => item.category).filter(Boolean)));
    return [...fromItems].sort((a, b) => formatAlbumLabel(a).localeCompare(formatAlbumLabel(b), "fr"));
  }, [items]);
  const albumCards = useMemo(() => buildAlbumGroups(items), [items]);
  const totalAlbumPages = Math.max(1, Math.ceil(albumCards.length / ALBUMS_PER_PAGE));
  const paginatedAlbumCards = useMemo(() => {
    const startIndex = (albumPage - 1) * ALBUMS_PER_PAGE;
    return albumCards.slice(startIndex, startIndex + ALBUMS_PER_PAGE);
  }, [albumCards, albumPage]);

  const albumSuggestions = useMemo(() => {
    const existing = new Set(albums.map((album) => album.toLowerCase()));
    const defaults = DEFAULT_ALBUMS.filter((album) => !existing.has(album.toLowerCase()));
    return [...albums, ...defaults];
  }, [albums]);

  const filteredItems = useMemo(
    () => (selectedAlbum === "tous" ? [] : items.filter((item) => item.category === selectedAlbum)),
    [items, selectedAlbum]
  );
  const selectedPageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredItems]);
  const selectedPageGroups = useMemo(() => groupByAlbum(selectedPageItems), [selectedPageItems]);
  const selectedPageAlbumGroups = useMemo(
    () =>
      Object.entries(selectedPageGroups).sort(([firstAlbum], [secondAlbum]) =>
        formatAlbumLabel(firstAlbum).localeCompare(formatAlbumLabel(secondAlbum), "fr")
      ),
    [selectedPageGroups]
  );
  const albumsCount = albums.length;
  const videosCount = useMemo(() => items.filter((item) => item.mediaType === "video").length, [items]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const pageItemIds = useMemo(() => selectedPageItems.map((item) => item.id), [selectedPageItems]);
  const allPageItemsSelected = pageItemIds.length > 0 && pageItemIds.every((id) => selectedIds.has(id));

  useEffect(() => {
    setCurrentPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
    setAlbumPage(1);
    if (selectedAlbum !== "tous") {
      setForm((current) => ({
        ...current,
        album: selectedAlbum
      }));
    }
    setSelectedIds(new Set());
  }, [selectedAlbum]);

  useEffect(() => {
    setAlbumPage((page) => Math.min(page, totalAlbumPages));
  }, [totalAlbumPages]);

  const toggleCurrentPageSelection = () => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (allPageItemsSelected) {
        pageItemIds.forEach((id) => next.delete(id));
      } else {
        pageItemIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  return (
    <div className="space-y-6 relative">
      <style>{`
        @keyframes album-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>

      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg bg-emerald-500 text-white text-sm font-medium flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-lg"></i>
          {toast}
        </div>
      )}

      {selectedItem ? (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setSelectedItem(null)}>
          <div
            className="w-full max-w-5xl rounded-[28px] bg-[#111111] border border-white/10 overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.4)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white truncate">{selectedItem.title}</h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white">
                    Album : {formatAlbumLabel(selectedItem.category)}
                  </span>
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-[#E8521A] text-white">
                    {selectedItem.mediaType === "video" ? "Vidéo" : "Photo"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/15 text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="bg-black flex items-center justify-center p-4 md:p-6">
              {selectedItem.mediaType === "video" ? (
                <video
                  src={selectedItem.src}
                  className="w-full max-h-[70vh] rounded-2xl bg-black"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={selectedItem.src}
                  alt={selectedItem.title}
                  className="w-full max-h-[70vh] object-contain rounded-2xl bg-black"
                />
              )}
            </div>

            <div className="px-5 py-4 border-t border-white/10 text-sm text-white/75">
              <p className="font-medium text-white">Descriptif</p>
              <p className="mt-2 leading-relaxed">{selectedItem.title}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Galerie Cloudinary</h1>
          <p className="text-gray-500 text-sm mt-1">
            Le titre commun est le nom de l'album. Chaque événement possède son bloc, et tu peux entrer dedans pour ajouter ou gérer ses médias.
          </p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <button
          onClick={() => void loadGallery()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-gray-600 text-sm font-medium border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A] transition-colors whitespace-nowrap self-start sm:self-auto"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#E8521A]">{items.length}</div>
          <div className="text-sm text-gray-500 mt-1">Médias publiés</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#1A1A1A]">{albumsCount}</div>
          <div className="text-sm text-gray-500 mt-1">Albums créés</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#1A8C7A]">{videosCount}</div>
          <div className="text-sm text-gray-500 mt-1">Vidéos publiées</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px,1fr] gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4 h-fit">
          <div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">Ajouter des médias</h2>
            <p className="text-sm text-gray-500 mt-1">
              Indique le nom de l'album ou entre dans un album existant, puis ajoute une photo, une vidéo ou tout un lot.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre commun / nom de l'album *</label>
            <input
              type="text"
              list="gallery-albums"
              value={form.album}
              onChange={(event) => setForm((current) => ({ ...current, album: event.target.value }))}
              placeholder="Ex: Tournoi inter-entreprises 2026"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
            />
            <datalist id="gallery-albums">
              {albumSuggestions.map((album) => (
                <option key={album} value={formatAlbumLabel(album)} />
              ))}
            </datalist>
            <div className="flex flex-wrap gap-2 mt-2">
              {albumSuggestions.slice(0, 6).map((album) => (
                <button
                  key={album}
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, album: formatAlbumLabel(album) }))}
                  className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-[#E8521A] text-xs font-medium transition-colors"
                >
                  {formatAlbumLabel(album)}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Tous les fichiers publiés ici iront dans cet album. Maximum {MAX_GALLERY_MEDIA_FILES} fichiers par envoi.
            </p>
          </div>

          <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-8 text-center">
            <i className="ri-upload-cloud-2-line text-4xl text-[#E8521A]"></i>
            <p className="mt-3 text-sm font-semibold text-[#1A1A1A]">Photos et vidéos</p>
            <p className="mt-1 text-sm text-gray-500">Tu peux choisir jusqu'à {MAX_GALLERY_MEDIA_FILES} fichiers en même temps.</p>
            <button
              type="button"
              onClick={openFilePicker}
              className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors"
            >
              <i className="ri-add-line"></i>
              Ajouter des fichiers
            </button>
          </div>

          {previews.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {previews.slice(0, 6).map((preview) => (
                <div key={preview.id} className="rounded-2xl overflow-hidden border border-gray-100 bg-[#FAF9F7]">
                  {preview.mediaType === "video" ? (
                    <video src={preview.url} className="w-full h-32 object-cover" muted playsInline controls />
                  ) : (
                    <img src={preview.url} alt={preview.name} className="w-full h-32 object-cover" />
                  )}
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-medium text-gray-700 line-clamp-2">{toTitleCase(preview.name)}</p>
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(preview.id)}
                        className="w-6 h-6 rounded-full bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center flex-shrink-0"
                        title="Retirer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                    <span className="inline-flex mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-black text-white">
                      {preview.mediaType === "video" ? "Vidéo" : "Photo"}
                    </span>
                  </div>
                </div>
              ))}
              {previews.length > 6 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 flex items-center justify-center text-sm font-medium text-gray-500">
                  +{previews.length - 6} média(s)
                </div>
              ) : null}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-[#FAF9F7] px-4 py-10 text-center text-sm text-gray-400">
              Les aperçus des médias sélectionnés apparaîtront ici
            </div>
          )}

          {form.files.length > 0 ? (
            <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-[#9A3E18]">
              <span className="font-semibold">{form.files.length}</span> fichier(s) prêt(s) à publier dans{" "}
              <span className="font-semibold">{normalizeAlbumValue(form.album) || "un album"}</span>.
            </div>
          ) : null}

          <div className="flex gap-3">
            <button
              onClick={resetForm}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Publication..." : "Publier"}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-[#1A1A1A]">Albums</h2>
            <p className="text-sm text-gray-500">Chaque événement peut avoir son album dédié.</p>
              </div>
              {selectedIds.size > 0 ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Désélectionner
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteSelection()}
                    disabled={deletingSelection}
                    className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {deletingSelection ? "Suppression..." : `Supprimer la sélection (${selectedIds.size})`}
                  </button>
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedAlbum("tous")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedAlbum === "tous" ? "bg-black text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Vue albums
              </button>
              {albums.map((album) => (
                <button
                  key={album}
                  type="button"
                  onClick={() => setSelectedAlbum(album)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedAlbum === album ? "bg-[#E8521A] text-white" : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-[#E8521A]"
                  }`}
                >
                  {formatAlbumLabel(album)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 h-80 animate-pulse"></div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-16 text-center">
              <i className="ri-image-add-line text-4xl text-gray-300"></i>
              <p className="mt-4 text-sm font-medium text-gray-600">Aucun média dans la galerie pour le moment</p>
              <p className="mt-1 text-sm text-gray-400">Publie ton premier lot à gauche pour commencer.</p>
            </div>
          ) : selectedAlbum === "tous" ? (
            albumCards.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-16 text-center">
                <i className="ri-folder-image-line text-4xl text-gray-300"></i>
                <p className="mt-4 text-sm font-medium text-gray-600">Aucun album pour le moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {paginatedAlbumCards.map((album) => (
                  <button
                    key={album.name}
                    type="button"
                    onClick={() => setSelectedAlbum(album.name)}
                    className="group relative h-80 overflow-hidden rounded-[28px] border border-gray-100 bg-black text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                  >
                    {album.cover.mediaType === "video" ? (
                      <video src={album.cover.src} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" muted playsInline preload="metadata" />
                    ) : (
                      <img src={album.cover.src} alt={album.name} className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/10"></div>
                    <AlbumPreviewStrip items={album.items} />
                    <div className="absolute inset-x-0 top-0 p-5">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-[#1A1A1A]">
                        <i className="ri-folder-image-line text-[#E8521A]"></i>
                        Album
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-20 p-5">
                      <h3 className="text-xl font-extrabold text-white">{formatAlbumLabel(album.name)}</h3>
                      <p className="mt-2 text-sm text-white/75">
                        {album.items.length} média(s) · {album.photosCount} photo(s) · {album.videosCount} vidéo(s)
                      </p>
                      <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#E8521A] px-4 py-2 text-sm font-semibold text-white">
                        Entrer dans l'album
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : filteredItems.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-16 text-center">
              <i className="ri-folder-image-line text-4xl text-gray-300"></i>
              <p className="mt-4 text-sm font-medium text-gray-600">Aucun média dans cet album.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3">
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-[#1A1A1A]">Album {formatAlbumLabel(selectedAlbum)}</span> · {filteredItems.length} média(s)
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedAlbum("tous")}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-arrow-left-line mr-1"></i>
                    Retour aux albums
                  </button>
                  <button
                    type="button"
                    onClick={toggleCurrentPageSelection}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:border-[#E8521A] hover:text-[#E8521A] transition-colors"
                  >
                    {allPageItemsSelected ? "Désélectionner la page" : "Sélectionner la page"}
                  </button>
                </div>
              </div>

              {selectedPageAlbumGroups.map(([album, albumItems]) => (
                <section key={album} className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-bold text-[#1A1A1A]">{formatAlbumLabel(album)}</h3>
                      <p className="text-sm text-gray-500">{albumItems.length} média(s) sur cette page</p>
                    </div>
                    <span className="hidden sm:inline-flex px-3 py-1 rounded-full bg-orange-50 text-[#E8521A] text-xs font-semibold">
                      Album
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {albumItems.map((item) => (
                      <div key={item.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-colors">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setSelectedItem(item)}
                            className="block w-full text-left cursor-zoom-in"
                          >
                            {item.mediaType === "video" ? (
                              <div className="relative">
                                <video src={item.src} className="w-full h-52 object-cover bg-black" playsInline preload="metadata" />
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                  <span className="w-14 h-14 rounded-full bg-white/85 text-[#1A1A1A] flex items-center justify-center">
                                    <i className="ri-play-fill text-2xl"></i>
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <img src={item.src} alt={item.title} className="w-full h-52 object-cover" />
                            )}
                          </button>

                          <label
                            className="absolute top-3 left-3 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm cursor-pointer"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(item.id)}
                              onChange={() => toggleSelectedItem(item.id)}
                              className="accent-[#E8521A]"
                            />
                            Sélectionner
                          </label>
                        </div>

                        <div className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <button type="button" onClick={() => setSelectedItem(item)} className="text-left flex-1 min-w-0">
                              <h3 className="text-sm font-bold text-[#1A1A1A] line-clamp-2 hover:text-[#E8521A] transition-colors">{item.title}</h3>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-[#E8521A]">
                                  {formatAlbumLabel(item.category)}
                                </span>
                                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-black text-white">
                                  {item.mediaType === "video" ? "Vidéo" : "Photo"}
                                </span>
                              </div>
                            </button>
                            <button
                              onClick={() => void handleDelete(item.id)}
                              className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors flex-shrink-0"
                              title="Supprimer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}

              {totalPages > 1 ? (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemLabel="médias"
                />
              ) : null}
            </>
          )}

          {!loading && selectedAlbum === "tous" && albumCards.length > 0 ? (
            <PaginationControls
              currentPage={albumPage}
              totalPages={totalAlbumPages}
              onPageChange={setAlbumPage}
              itemLabel="albums"
            />
          ) : null}
        </div>
      </div>

      <input
        id="gallery-media-picker"
        key={`gallery-${fileInputKey}`}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={(event) => appendFiles(Array.from(event.target.files ?? []))}
        className="hidden"
      />
    </div>
  );
}
